/**
 * What a customer sees in their Defera Credits history (Profile → Defera
 * Credits → Credit history).
 *
 * `defera_credit_ledger.description` is an INTERNAL note. Admins type their
 * reason there ("goodwill — retention risk", a case number), and one-off fix
 * scripts left whole investigations in it. Until 2026-10-11 the customer's own
 * balance endpoint returned it and the wallet card printed it under every row
 * (and the owner policy from migration 075 let a customer read it straight from
 * the database API — closed by migration 178).
 *
 * So a customer's entry is built from structured fields only:
 *   - the entry type gives the row title ("Credit added", "Credit applied", …);
 *   - `customer_note`, the one free-text field written FOR the customer (the
 *     "Shown to the customer" box on the admin grant form), is the line under it;
 *   - with no note, we write that line ourselves from how the entry was made:
 *     the idempotency-key prefix a system writer chose (birthday-credit-,
 *     cancel-credit-, earn-credit:), `related_object.source`, the trip reference.
 * `description` is never read here, and CustomerCreditEntry has no field that
 * could carry it.
 *
 * No Deno or npm: imports, so vitest tests it directly
 * (defera-credit-history.test.ts).
 */

/** Longest "Shown to the customer" note. Migration 178's CHECK matches it. */
export const CUSTOMER_NOTE_MAX_LENGTH = 200;

/** Row titles. The help articles quote these words (src/content/help). */
export const CREDIT_ENTRY_LABELS: Readonly<Record<string, string>> = {
  credit: "Credit added",
  debit: "Credit applied",
  adjustment: "Adjustment",
  hold: "Held for checkout",
  release: "Returned to balance",
};

/** A defera_credit_ledger row as the server reads it. Other columns
 *  (description, created_by, …) may be present and are never read. */
export interface CreditLedgerRow {
  id: string;
  type: string;
  amount_cents: number | string;
  status: string;
  created_at: string;
  trip_reference?: string | null;
  related_object?: Record<string, unknown> | null;
  /** Added by migration 178; absent on a database that doesn't have it yet. */
  customer_note?: string | null;
  [column: string]: unknown;
}

/** One row of the customer's Credit history. Every field is customer-safe. */
export interface CustomerCreditEntry {
  id: string;
  type: string;
  amount_cents: number;
  status: string;
  trip_reference: string | null;
  created_at: string;
  /** Row title: "Credit added", "Credit applied", … */
  label: string;
  /** The line under it ("Birthday gift", "Applied to trip ABC123"), or "". */
  detail: string;
}

/**
 * Tidy a "Shown to the customer" note for storage: one line, trimmed, no
 * control characters. Empty means no note. Too long is an error rather than a
 * silent cut, so the admin sees exactly what the customer will.
 */
export function normalizeCustomerNote(
  input: unknown,
): { ok: true; note: string | null } | { ok: false; error: string } {
  if (input === null || input === undefined) return { ok: true, note: null };
  if (typeof input !== "string") return { ok: false, error: "customer_note must be text" };
  const note = oneLine(input);
  if (!note) return { ok: true, note: null };
  if (note.length > CUSTOMER_NOTE_MAX_LENGTH) {
    return { ok: false, error: `customer_note must be ${CUSTOMER_NOTE_MAX_LENGTH} characters or fewer` };
  }
  return { ok: true, note };
}

function oneLine(value: string): string {
  return value.replace(/\p{Cc}/gu, " ").replace(/\s+/g, " ").trim();
}

/** A trip reference is a short code (ABC123). Anything else in the column is
 *  left out rather than shown. */
function tripRef(value: unknown): string | null {
  const ref = String(value ?? "").trim();
  return /^[A-Za-z0-9-]{3,20}$/.test(ref) ? ref : null;
}

function idempotencyKey(row: CreditLedgerRow): string {
  return String(row.related_object?.idempotency_key ?? "");
}

const CANCEL_CREDIT_KEY =
  /^cancel-credit-(?:[a-z]+-)*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/**
 * The booking behind a cancel-for-credit grant, or null. grantCancellationCredit
 * keys every grant `cancel-credit-<bookingId>` (cancel-credit.ts, shared by the
 * three cancel functions and the defaulted-trip opt-in), and the correction
 * scripts kept the prefix (`cancel-credit-correction-<bookingId>`). The trip
 * reference is looked up from the booking, not read out of the description.
 */
export function cancellationCreditBookingId(row: CreditLedgerRow): string | null {
  const match = idempotencyKey(row).match(CANCEL_CREDIT_KEY);
  return match ? match[1].toLowerCase() : null;
}

function fromCancelledTrip(ref: string | null): string {
  return ref ? `From cancelled trip ${ref}` : "From a cancelled trip";
}

/** The line under an entry's title. */
function entryDetail(row: CreditLedgerRow, tripRefByBookingId: Record<string, string>): string {
  if (typeof row.customer_note === "string") {
    const note = oneLine(row.customer_note).slice(0, CUSTOMER_NOTE_MAX_LENGTH);
    if (note) return note;
  }

  const key = idempotencyKey(row);
  if (key.startsWith("birthday-credit-")) return "Birthday gift";
  // Earn Defera Credit approvals (feature/earn-credits) grant through
  // defera-credit-admin, keyed earn-credit:<submission>.
  if (key.startsWith("earn-credit:")) return "Creator reward";
  const cancelledBookingId = cancellationCreditBookingId(row);
  if (cancelledBookingId) return fromCancelledTrip(tripRefByBookingId[cancelledBookingId] ?? null);

  const ref = tripRef(row.trip_reference);
  switch (row.related_object?.source) {
    case "cancellation_restore":
      return fromCancelledTrip(ref);
    case "customer_apply":
      return ref ? `Applied to upcoming payments on trip ${ref}` : "Applied to upcoming payments";
    case "checkout_reversal":
      return ref ? `The payment for trip ${ref} didn't go through` : "A payment didn't go through";
  }

  switch (row.type) {
    case "debit":
      return ref ? `Applied to trip ${ref}` : "";
    case "release":
      return ref ? `From trip ${ref}` : "";
    case "hold":
      return ref ? `For trip ${ref}` : "";
    case "credit":
      return "From the Defera team";
    case "adjustment":
      return "Made by the Defera team";
    default:
      return "";
  }
}

/** Ledger rows → the customer's Credit history. `tripRefByBookingId` (lower-
 *  case booking id → trip reference) names the trip on cancellation credits. */
export function toCustomerCreditHistory(
  rows: CreditLedgerRow[] | null | undefined,
  tripRefByBookingId: Record<string, string> = {},
): CustomerCreditEntry[] {
  return (rows ?? []).filter(Boolean).map((row) => ({
    id: String(row.id),
    type: String(row.type),
    amount_cents: Math.round(Number(row.amount_cents) || 0),
    status: String(row.status),
    trip_reference: tripRef(row.trip_reference),
    created_at: String(row.created_at),
    label: Object.hasOwn(CREDIT_ENTRY_LABELS, row.type) ? CREDIT_ENTRY_LABELS[row.type] : "Activity",
    detail: entryDetail(row, tripRefByBookingId),
  }));
}

/**
 * Read a customer's newest ledger rows and return them as Credit history.
 * Pass the service-role client (the ledger is server-only since migration 178).
 *
 * `select("*")` on purpose: it works before and after migration 178 adds
 * customer_note, so deploying this before applying the migration can't take
 * down the balance endpoint that checkout reads. toCustomerCreditHistory is the
 * allow-list; nothing else from the row leaves.
 *
 * Throws if the ledger read fails. A failed trip-reference lookup only costs
 * the trip code on cancellation credits ("From a cancelled trip").
 */
export async function loadCustomerCreditHistory(
  // deno-lint-ignore no-explicit-any
  supabase: any,
  userId: string,
  limit = 50,
): Promise<CustomerCreditEntry[]> {
  const { data, error } = await supabase
    .from("defera_credit_ledger")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message || "defera_credit_ledger read failed");
  const rows = (data ?? []) as CreditLedgerRow[];

  const bookingIds = [...new Set(rows.map(cancellationCreditBookingId).filter((id): id is string => !!id))];
  const tripRefByBookingId: Record<string, string> = {};
  if (bookingIds.length > 0) {
    try {
      const { data: bookings } = await supabase
        .from("bookings")
        .select("id, trip_reference, booking_reference")
        .eq("user_id", userId)
        .in("id", bookingIds);
      for (const b of (bookings ?? []) as Array<{ id: string; trip_reference?: string | null; booking_reference?: string | null }>) {
        const ref = tripRef(b.trip_reference) ?? tripRef(b.booking_reference);
        if (ref) tripRefByBookingId[String(b.id).toLowerCase()] = ref;
      }
    } catch (_e) {
      // Fall back to "From a cancelled trip".
    }
  }

  return toCustomerCreditHistory(rows, tripRefByBookingId);
}

/**
 * Store the "Shown to the customer" note on the ledger row a grant just wrote
 * (or replayed). atomic_defera_credit_grant takes no note, so this is a second
 * write; it never throws, because the grant has already happened and an error
 * here must not read as a failed grant (the admin would grant again).
 */
export async function saveCustomerNote(
  // deno-lint-ignore no-explicit-any
  supabase: any,
  { ledgerId, userId, note }: { ledgerId: string; userId: string; note: string },
): Promise<{ saved: boolean; error?: string }> {
  try {
    const { data, error } = await supabase
      .from("defera_credit_ledger")
      .update({ customer_note: note })
      .eq("id", ledgerId)
      .eq("user_id", userId)
      .select("id");
    if (error) return { saved: false, error: error.message || "update failed" };
    return Array.isArray(data) && data.length === 1
      ? { saved: true }
      : { saved: false, error: "ledger row not found" };
  } catch (e) {
    return { saved: false, error: e instanceof Error ? e.message : String(e) };
  }
}
